import React, { useState, useEffect, useRef } from "react";
import { Box, TextField, Button, Typography, Alert } from "@mui/material";
import { useForm } from "react-hook-form";
import SendIcon from "@mui/icons-material/Send";
import { cancelButtonStyle } from "../../styles/buttonStyles";
import { submitContactFormApiPublicContactPost } from "../../api/generated";
import type { ContactSubmissionCreate } from "../../api/generated";
import {
  trackContactFormSubmit,
  trackFormFieldInteraction,
} from "../../utils/analytics";

interface ContactFormData {
  name: string;
  email: string;
  message: string;
}

export const ContactForm: React.FC = () => {
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const onSubmit = async (data: ContactFormData) => {
    try {
      const submissionData: ContactSubmissionCreate = {
        name: data.name,
        email: data.email,
        message: data.message,
      };

      const response = await submitContactFormApiPublicContactPost({
        body: submissionData,
      });

      if (!response.data) {
        throw new Error("Failed to send message");
      }

      setSubmitSuccess(true);
      setSubmitError(false);
      reset();

      // Track successful form submission in GA4
      trackContactFormSubmit(true);

      // Clear any previous timeout
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }

      // Hide success message after 5 seconds
      successTimeoutRef.current = setTimeout(() => {
        setSubmitSuccess(false);
        successTimeoutRef.current = null;
      }, 5000);
    } catch (error) {
      console.error("Error submitting contact form:", error);
      setSubmitError(true);
      setSubmitSuccess(false);

      // Track failed form submission in GA4
      trackContactFormSubmit(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontFamily: '"Playfair Display", serif',
          fontSize: "1.5rem",
          fontWeight: 500,
          mb: 1,
          textAlign: "center",
        }}
      >
        Send a Message
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: '"Playfair Display", serif',
          color: "text.secondary",
          mb: 4,
          textAlign: "center",
        }}
      >
        Fill out the form below and I'll get back to you as soon as possible.
      </Typography>

      {submitSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Thank you for your message! I'll get back to you soon.
        </Alert>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Something went wrong. Please try again later.
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          fullWidth
          label="Name"
          margin="normal"
          {...register("name", {
            required: "Name is required",
            minLength: {
              value: 2,
              message: "Name must be at least 2 characters",
            },
          })}
          onFocus={() =>
            trackFormFieldInteraction("contact_form", "name", "focus")
          }
          onBlur={() =>
            trackFormFieldInteraction("contact_form", "name", "blur")
          }
          onChange={(e) => {
            register("name").onChange(e);
            if (e.target.value.length > 0) {
              trackFormFieldInteraction("contact_form", "name", "change");
            }
          }}
          error={!!errors.name}
          helperText={errors.name?.message}
          aria-describedby={errors.name ? "name-error" : undefined}
          disabled={isSubmitting}
          sx={{
            mb: 2,
            "&:hover .MuiInputLabel-root": {
              fontWeight: 600,
            },
            "& .MuiInputBase-input::placeholder": {
              fontSize: "0.875rem",
            },
          }}
        />

        <TextField
          fullWidth
          label="Email"
          type="email"
          margin="normal"
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          })}
          onFocus={() =>
            trackFormFieldInteraction("contact_form", "email", "focus")
          }
          onBlur={(e) => {
            register("email").onBlur(e);
            if (e.target.value.length > 0) {
              trackFormFieldInteraction("contact_form", "email", "change");
            }
            trackFormFieldInteraction("contact_form", "email", "blur");
          }}
          error={!!errors.email}
          helperText={errors.email?.message}
          aria-describedby={errors.email ? "email-error" : undefined}
          disabled={isSubmitting}
          sx={{
            mb: 2,
            "&:hover .MuiInputLabel-root": {
              fontWeight: 600,
            },
            "& .MuiInputBase-input::placeholder": {
              fontSize: "0.875rem",
            },
          }}
        />

        <TextField
          fullWidth
          label="Message"
          multiline
          rows={2}
          margin="normal"
          data-allow-multiline="true"
          {...register("message", {
            required: "Message is required",
            minLength: {
              value: 10,
              message: "Message must be at least 10 characters",
            },
          })}
          onFocus={() =>
            trackFormFieldInteraction("contact_form", "message", "focus")
          }
          onBlur={(e) => {
            register("message").onBlur(e);
            if (e.target.value.length > 0) {
              trackFormFieldInteraction("contact_form", "message", "change");
            }
            trackFormFieldInteraction("contact_form", "message", "blur");
          }}
          error={!!errors.message}
          helperText={errors.message?.message}
          aria-describedby={errors.message ? "message-error" : undefined}
          disabled={isSubmitting}
          sx={{
            mb: 5,
            "&:hover .MuiInputLabel-root": {
              fontWeight: 600,
            },
            "& .MuiInputBase-input::placeholder": {
              fontSize: "0.875rem",
            },
          }}
        />

        <Button
          type="submit"
          variant="text"
          fullWidth
          size="large"
          endIcon={<SendIcon />}
          disabled={isSubmitting}
          disableRipple
          aria-busy={isSubmitting}
          aria-label={isSubmitting ? "Sending message" : "Send message"}
          sx={{
            ...cancelButtonStyle,
            py: 1.5,
            fontSize: { xs: "0.875rem", md: "1rem" },
            fontFamily: '"Playfair Display", serif',
            justifyContent: "center",
          }}
        >
          {isSubmitting ? "Sending..." : "Send Message"}
        </Button>
      </Box>
    </Box>
  );
};
